import React, { useState, useRef, useEffect, useCallback } from 'react';

import styles from './styles.module.css';

interface RangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  formatLabel?: (value: number) => string;
}

export default function RangeSlider({
  min,
  max,
  value,
  onChange,
  formatLabel
}: RangeSliderProps) {
  const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const getPercentage = (val: number) => {
    return ((val - min) / (max - min)) * 100;
  };

  const getValueFromPosition = (clientX: number) => {
    if (!trackRef.current) return min;

    const rect = trackRef.current.getBoundingClientRect();
    const percentage = Math.max(
      0,
      Math.min(1, (clientX - rect.left) / rect.width)
    );
    return Math.round(min + percentage * (max - min));
  };

  const handleMouseDown = (thumb: 'min' | 'max') => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(thumb);
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const newValue = getValueFromPosition(e.clientX);

      if (isDragging === 'min') {
        const newMin = Math.min(newValue, value[1] - 1);
        onChange([Math.max(min, newMin), value[1]]);
      } else {
        const newMax = Math.max(newValue, value[0] + 1);
        onChange([value[0], Math.min(max, newMax)]);
      }
    },
    [isDragging, value, min, max, onChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(null);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const leftPercentage = getPercentage(value[0]);
  const rightPercentage = getPercentage(value[1]);

  return (
    <div className={styles.container}>
      <div className={styles.sliderWrapper}>
        <div ref={trackRef} className={styles.track}>
          <div
            className={styles.trackActive}
            style={{
              left: `${leftPercentage}%`,
              right: `${100 - rightPercentage}%`
            }}
          />
        </div>

        <div
          className={`${styles.thumb} ${isDragging === 'min' ? styles.thumbActive : ''}`}
          style={{ left: `${leftPercentage}%` }}
          onMouseDown={handleMouseDown('min')}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value[0]}
          tabIndex={0}
        >
          {formatLabel && (
            <div className={styles.thumbLabel}>{formatLabel(value[0])}</div>
          )}
        </div>

        <div
          className={`${styles.thumb} ${isDragging === 'max' ? styles.thumbActive : ''}`}
          style={{ left: `${rightPercentage}%` }}
          onMouseDown={handleMouseDown('max')}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value[1]}
          tabIndex={0}
        >
          {formatLabel && (
            <div className={styles.thumbLabel}>{formatLabel(value[1])}</div>
          )}
        </div>
      </div>
    </div>
  );
}
